import { __ } from '@wordpress/i18n';
import { useBlockProps } from '@wordpress/block-editor';
import { useEffect, useMemo, useState} from 'react';
import { select, useSelect } from '@wordpress/data';
import {VayuBlockAdvanceStyle} from '@vayu-blocks/advance-panel/style.js';
import { VayuBlocksDevice} from '@vayu-blocks/device-control';
import { debounce } from '../utils/debounce';

export default function AdvanceSettings({ children, attributes, setAttributes ,clientId, isSelected}) {
   
    const device = VayuBlocksDevice();
    const [selectedBlockClass, setSelectedBlockClass] = useState(null);
    let transformstyle = 'none';
    let back_z_index = 0;
    let front_opacity = 1;
    let back_opacity = 1;

    const selectedBlockClientId = useSelect((select) => select('core/block-editor').getSelectedBlockClientId(),[]);

    const { mergedStyles, handleMouseEnter, handleMouseLeave,responsiveClasses, hoverAnimationClass } = VayuBlockAdvanceStyle(attributes);

    const style= {
        height: attributes.advheight[device],
        maxHeight: attributes.advheight[device],
        overflow: 'hidden',
    }

    const flipTransforms = {
        flip: {
            left: 'rotateY(-180deg)',
            right: 'rotateY(180deg)',
            top: 'rotateX(180deg)',
            bottom: 'rotateX(-180deg)',
        },
        'flip-z': 'rotateX(180deg) rotateZ(90deg)',
        'flip-x': 'rotateY(180deg) rotateZ(90deg)',
        'zoom-in': 'scale(0.5)',
        'zoom-out': '',
        'fade-in': '',
        slide: {
            left: 'translateX(-105%)',
            right: 'translateX(105%)',
            top: 'translateY(-105%)',
            bottom: 'translateY(105%)',
        },
        push: {
            left: 'translateX(-100%)',
            right: 'translateX(100%)',
            top: 'translateY(100%)',
            bottom: 'translateY(-100%)',
        },
    };

    if (['flip', 'slide', 'push'].includes(attributes.imagehvreffect)) {
        const sideMap = flipTransforms[attributes.imagehvreffect];
        transformstyle = sideMap?.[attributes.flipside] ?? 'none';
    } else {
        transformstyle = flipTransforms[attributes.imagehvreffect] ?? 'none';
    }

    const selectedBlock = useSelect(
        (select) => {

            if (!isSelected && !selectedBlockClientId) return null;

            // Check if the selected block is a child of this block
            const parentIds = select('core/block-editor').getBlockParents(selectedBlockClientId);

            // If this block is the direct parent
            if (parentIds.includes(clientId)) {
                return select('core/block-editor').getBlock(selectedBlockClientId);
            }

            return null;
        },
        [clientId, selectedBlockClientId,isSelected]
    );

   const getFlipBlockClass = (block) => {
        if (!block) return null;

        const { attributes, clientId, name } = block;
        const blockClass = attributes.className || '';

        if (name === 'vayu-blocks/flip-box') return 'wp-block-vayu-blocks-image-flip';
        if (blockClass) return blockClass;

        const parentIds = select('core/block-editor').getBlockParents(clientId);
        for (const id of parentIds) {
            const parentBlock = select('core/block-editor').getBlock(id);
            if (parentBlock?.name === 'vayu-blocks/flip-wrapper') {
                return parentBlock.attributes.className || null;
            }
        }

        return null;
    };

    useEffect(() => {
        if (isSelected && selectedBlock) {
            setSelectedBlockClass(getFlipBlockClass(selectedBlock));
        }
    }, [isSelected, selectedBlock]);

    if (isSelected) {
        // Adjust back opacity initially based on current state
        if (!attributes.selectedanimation) {
            back_opacity = 0;
        }

        if (selectedBlockClass?.includes('wp-block-vayu-blocks-image-flip')) {
            front_opacity = 1;

            if (!attributes.selectedanimation) {
                setAttributes({ selectedanimation: true });
            }

        } else if (selectedBlockClass?.includes('vayu_blocks_flip-box-front')) {
            back_z_index = 1;
            front_opacity = 1;

            if (attributes.selectedanimation !== false) {
                setAttributes({ selectedanimation: false });
            }

        } else if (selectedBlockClass?.includes('vayu_blocks_flip-box-back')) {
            back_z_index = 100;
            back_opacity = 1;
            transformstyle = 'none';
            front_opacity = 0;

            if (attributes.selectedanimation !== false) {
                setAttributes({ selectedanimation: false });
            }

        } else {
            back_z_index = 1;
            front_opacity = 1;
            back_opacity = 1;

            if (!attributes.selectedanimation) {
                setAttributes({ selectedanimation: true });
            }
        }
    }

    // Always apply this rule regardless of selection
    if (attributes.imagehvreffect === 'zoom-in') {
        back_opacity = 0;
    }

    const debouncedEnter = useMemo(() => debounce(handleMouseEnter, 100), []);
    const debouncedLeave = useMemo(() => debounce(handleMouseLeave, 100), []);


    const blockProps = useBlockProps({
        className: `block-${clientId} ${hoverAnimationClass} ${responsiveClasses}`,
        style: {
            ...mergedStyles,
            ...style,
           '--back-transform-style': transformstyle, 
           '--back-z-index' : back_z_index,
           '--front-opacity' :front_opacity,
           '--back-opacity' : back_opacity,
        },
        onMouseEnter: debouncedEnter,
        onMouseLeave: debouncedLeave,
    });

    return (
        <div {...blockProps}>
            {children}
        </div>
    );
}